// LocaleToggle — language picker in the Header.
//
// 8 supported locales (EN/FR row-column, ZH/ES/AR/DE/PT/JA on-demand). A
// segmented control would crowd the rail past 3 entries, so we render a
// compact <select> trimmed to look like the other code chips: monospace,
// 1px border, hover-amber, active-cyan. The trigger shows the active code
// ("EN" / "ZH" / …); the dropdown shows the language's own name to keep
// the picker legible without translating the chrome strings.
//
// TODO(rtl): when an RTL locale (ar/he) is active the chrome should flip.
// For now we set `dir="rtl"` on <html> (see lib/locale.tsx) so screen-
// readers and form inputs respect the language, but the layout stays LTR.
// Full RTL pass is a separate workstream.

import { LOCALE_LABELS, type Locale, SUPPORTED_LOCALES, useLocale } from '@/lib/locale';

export function LocaleToggle() {
  const { locale, setLocale } = useLocale();

  return (
    <div
      className="shell-locale-toggle"
      style={{
        display: 'inline-flex',
        alignItems: 'stretch',
        height: 22,
        border: '1px solid var(--bd)',
        fontFamily: 'var(--font-mono, monospace)',
        fontSize: 11,
        lineHeight: '20px',
        position: 'relative',
        userSelect: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          padding: '0 8px',
          color: 'var(--accent-cyan, var(--fg-1))',
          fontWeight: 600,
          letterSpacing: '0.04em',
          pointerEvents: 'none',
        }}
      >
        {LOCALE_LABELS[locale].code}
      </span>
      <select
        aria-label="Interface language"
        value={locale}
        onChange={(e) => setLocale(e.target.value as Locale)}
        style={{
          // Stretch the <select> over the chip so the chevron click-target
          // sits inside it; the chip above shows the active code visually.
          // opacity: 0 here would hide the native dropdown's <option> text
          // in some browsers (Chromium inherits parent color → invisible
          // items). Instead, push the visible value off-screen via
          // text-indent while keeping the element itself opaque-and-legible
          // for the dropdown panel.
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 'none',
          background: 'transparent',
          // Real foreground so the OS dropdown items render legibly in
          // both light + dark modes (matches the rest of the chrome).
          color: 'var(--fg-1)',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          // Hide the collapsed value + caret without making the dropdown
          // unreadable: indent text out of view, kill the focus ring on
          // the absolute layer (the chip's border already conveys focus).
          textIndent: '-9999px',
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
        }}
      >
        {SUPPORTED_LOCALES.map((l) => (
          <option
            key={l}
            value={l}
            style={{
              // Force explicit colors on the option panel — Chromium will
              // otherwise inherit `color: transparent` etc. from the
              // parent select on macOS and render invisible items.
              background: 'var(--bg-pane)',
              color: 'var(--fg-1)',
              textIndent: 0,
            }}
          >
            {LOCALE_LABELS[l].code} — {LOCALE_LABELS[l].native}
          </option>
        ))}
      </select>
    </div>
  );
}

export type { Locale };
